import { useState, useCallback, useRef, useLayoutEffect, useEffect, useMemo, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Project, type ReadyMonthStatRow, type Session } from '@/api/client';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/useAuth';
import type { TableColumn } from '@/hooks/useTableColumns';
import {
  useProjectsTableColumns,
  ALL_COLUMN_DEFS,
  getNoComboColumnIds,
  READERS_COLUMN_WIDTH_COMPACT,
  STUDIO_COLUMN_WIDTH_COMPACT,
} from '@/hooks/useTableColumns';
import { useTableCompactMode } from '@/hooks/useTableCompactMode';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { useMiddleButtonPanScroll } from '@/hooks/useMiddleButtonPanScroll';
import { useAnimatedPopover } from '@/hooks/useAnimatedPopover';
import { useTypeBadgeVisibility } from '@/hooks/useTypeBadgeVisibility';
import { usePlannerNameBadgeMode } from '@/hooks/usePlannerNameBadgeMode';
import { PROJECT_COLUMN_FILTER_TYPES } from '@/lib/tableFilters';
import { useProjectsTableFilters } from '@/contexts/ProjectsTableFiltersContext';
import { getTableRowHeightPx } from '@/utils/cssTokens';
import { HIGHLIGHT_PARAM, HIGHLIGHT_TYPE_PROJECT, useHighlightTarget } from '@/hooks/useTeleportToTarget';
import { ProjectTableRow } from './ProjectTableRow';
import { ComboHeaderCell } from './ComboHeaderCell';
import { HeaderCellWithWidth } from './HeaderCellWithWidth';
import { TableContextMenu } from './TableContextMenu';
import { ProjectSessionsModal } from '@/components/ProjectSessionsModal/ProjectSessionsModal';
import { getProjectSessionsQueryOptions } from '@/components/ProjectSessionsModal/projectSessionsQuery';
import { SessionModal } from '@/components/SessionDialog/SessionModal';
import { ProjectNotesPopover } from '@/components/ProjectNotesPopover/ProjectNotesPopover';
import { getProjectNotesQueryOptions } from '@/components/ProjectNotesPopover/projectNotesPopoverQuery';
import { ProjectFilesPopover } from '@/components/ProjectFilesPopover/ProjectFilesPopover';
import { getProjectFilesQueryOptions } from '@/components/ProjectFilesPopover/projectFilesPopoverQuery';
import { useColumnDragDrop } from './useColumnDragDrop';
import { ReadyMonthSeparatorBar, ReadyMonthSeparatorSpacer } from './ReadyMonthSeparatorRow';
import { ReadyMonthSeparatorContextMenu } from './ReadyMonthSeparatorContextMenu';
import type { ReadyMonthSeparatorDisplay } from '@/hooks/useReadyMonthSeparatorDisplay';
import {
  formatMonthLabelRu,
  formatSecondsToHMS,
  parseYearMonthKey,
  summarizeLoadedProjectsWithTime,
  summarizeNonReadyProjectsWithTime,
} from '@/utils/readyMonthStats';
import { CellCommentsTableProvider } from '@/features/project-cell-comments/CellCommentsTableContext';
import { patchProjectListCaches } from '@/lib/projectListQueryCache';
import { getProjectEntityFromCaches } from '@/lib/projectEntityCache';
import { useProjectTableSummaries } from '@/hooks/useProjectTableSummaries';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';
import { measureTableVirtualRowElement } from '@/utils/tableVirtualMeasure';
import './ProjectsTable.css';

const IS_TOUCH_DEVICE =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || window.matchMedia('(pointer: coarse)').matches);

const EMPTY_HIDE_COLUMN_IDS: readonly string[] = [];
const HAS_MUSIC_HIDE_IDS: readonly string[] = ['has_music'];

type TableVirtualRow =
  | { type: 'month'; monthKey: string; stat: ReadyMonthStatRow; isLatestMonth: boolean }
  | { type: 'archive' }
  | { type: 'project'; projectId: string };

type ProjectResourceLoadingKind = 'sessions' | 'notes' | 'files';

interface ProjectsTableProps {
  isLoading?: boolean;
  isSyncing?: boolean;
  /** Активные (не «Готов») проекты — блок после разделителей месяцев */
  projectIds: string[];
  onDelete: (id: string) => void;
  onStateChange: (projectId: string, newState: string) => void;
  onProjectTypeChange: (projectId: string, newType: import('@/api/client').ProjectType | null) => void;
  onOpenReviewCheckEmail?: (project: Project) => void;
  onOpenProductionHandoffEmail?: (project: Project) => void;
  onOpenPreArchiveUploadEmail?: (project: Project) => void;
  onOpenMountingCompleteEmail?: (project: Project) => void;
  onOpenCorrectionsAppliedEmail?: (project: Project) => void;
  onOpenProjectReviewedEmail?: (project: Project) => void;
  /** Только для этой строки true — иначе при удалении перерисовывалась бы вся таблица */
  deletingProjectId: string | null;
  /** Месяцы с project_completed (порядок: старые выше); агрегаты на разделителе — только из кэша после раскрытия */
  readyMonthStats?: ReadyMonthStatRow[];
  expandedReadyMonths?: Record<string, boolean>;
  onToggleReadyMonth?: (monthKey: string) => void;
  /** Загруженные при раскрытии готовые по ключу YYYY-MM */
  readyProjectIdsByMonth?: Record<string, string[]>;
  allProjectsForFilter?: Project[];
  readyMonthProjectLoading?: Record<string, boolean>;
  monthSepDisplay?: ReadyMonthSeparatorDisplay;
  onMonthSepShowReadyTime?: (value: boolean) => void;
  onMonthSepShowInProgress?: (value: boolean) => void;
  onMonthSepShowProjectCount?: (value: boolean) => void;
  onMonthSepShowActiveProjectsCount?: (value: boolean) => void;
  onExpandAllReadyMonths?: () => void;
  onCollapseAllReadyMonths?: () => void;
  /** Блок «Архив» (спойлер внизу); count=0 — не показывать */
  archivedProjectCount?: number;
  expandedArchive?: boolean;
  onToggleArchive?: () => void;
  archivedProjectIds?: string[];
  archivedProjectLoading?: boolean;
  /** Глобальный поиск по шапке: только строки результатов, без блоков месяцев/архива */
  globalSearchActive?: boolean;
  globalSearchProjectIds?: string[];
  /** Карточки из ответа поиска (готовые под спойлером ещё не в month-кэше) */
  globalSearchProjectsById?: ReadonlyMap<string, Project>;
  /** Идёт запрос поиска — пустой список не показывать как «Нет проектов» */
  globalSearchFetching?: boolean;
  /** Слова debounced-поиска — подсветка в текстовых ячейках (актуально в режиме глобального поиска) */
  searchHighlightWords?: string[];
}

const DEFAULT_MONTH_SEP_DISPLAY: ReadyMonthSeparatorDisplay = {
  showReadyTime: true,
  showInProgress: true,
  showProjectCount: true,
  showActiveProjectsCount: true,
};

/** Должно совпадать с фиксированной высотой `.projects-table__month-sep-wrap` в CSS */
const MONTH_SEP_ESTIMATE_PX = 50;

function ProjectsTableInner({
  isLoading = false,
  isSyncing = false,
  projectIds,
  onDelete,
  onStateChange,
  onProjectTypeChange,
  onOpenReviewCheckEmail,
  onOpenProductionHandoffEmail,
  onOpenPreArchiveUploadEmail,
  onOpenMountingCompleteEmail,
  onOpenCorrectionsAppliedEmail,
  onOpenProjectReviewedEmail,
  deletingProjectId,
  readyMonthStats = [],
  expandedReadyMonths = {},
  onToggleReadyMonth,
  readyProjectIdsByMonth = {},
  allProjectsForFilter = [],
  readyMonthProjectLoading = {},
  monthSepDisplay = DEFAULT_MONTH_SEP_DISPLAY,
  onMonthSepShowReadyTime,
  onMonthSepShowInProgress,
  onMonthSepShowProjectCount,
  onMonthSepShowActiveProjectsCount,
  onExpandAllReadyMonths,
  onCollapseAllReadyMonths,
  archivedProjectCount = 0,
  expandedArchive = false,
  onToggleArchive,
  archivedProjectIds = [],
  archivedProjectLoading = false,
  globalSearchActive = false,
  globalSearchProjectIds = [],
  globalSearchProjectsById,
  globalSearchFetching = false,
  searchHighlightWords,
}: ProjectsTableProps) {
  void isSyncing;
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const resolveProject = useCallback(
    (projectId: string) => {
      if (globalSearchActive && globalSearchProjectsById) {
        const fromSearch = globalSearchProjectsById.get(projectId);
        if (fromSearch) return fromSearch;
      }
      return getProjectEntityFromCaches(queryClient, projectId);
    },
    [queryClient, globalSearchActive, globalSearchProjectsById]
  );
  const parentRef = useRef<HTMLDivElement | null>(null);
  const headerRowRef = useRef<HTMLDivElement>(null);
  const [scrollReady, setScrollReady] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorColumnId, setMenuAnchorColumnId] = useState<string | null>(null);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ x: number; y: number } | null>(null);
  const [monthSepMenuOpen, setMonthSepMenuOpen] = useState(false);
  const [monthSepMenuPosition, setMonthSepMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [sessionEditFromPopover, setSessionEditFromPopover] = useState<Session | null>(null);
  const [headerHeight, setHeaderHeight] = useState(44);
  const [resourceLoadingByKey, setResourceLoadingByKey] = useState<Record<string, true>>({});

  const sessionsPopover = useAnimatedPopover<{ project: Project; anchorEl: HTMLElement }>(
    (item) => item.project.id
  );
  const notesPopover = useAnimatedPopover<{ project: Project; anchorEl: HTMLElement }>(
    (item) => item.project.id
  );
  const filesPopover = useAnimatedPopover<{ project: Project; anchorEl: HTMLElement }>(
    (item) => item.project.id
  );

  const setProjectResourceLoading = useCallback(
    (kind: ProjectResourceLoadingKind, projectId: string, next: boolean) => {
      const key = `${kind}:${projectId}`;
      setResourceLoadingByKey((prev) => {
        if (next) {
          if (prev[key]) return prev;
          return { ...prev, [key]: true };
        }
        if (!prev[key]) return prev;
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    },
    []
  );

  const isProjectResourceLoading = useCallback(
    (kind: ProjectResourceLoadingKind, projectId: string) => Boolean(resourceLoadingByKey[`${kind}:${projectId}`]),
    [resourceLoadingByKey]
  );

  const handleOpenSessionsModal = useCallback(
    async (project: Project, anchorEl: HTMLElement) => {
      if (isProjectResourceLoading('sessions', project.id)) return;
      setProjectResourceLoading('sessions', project.id, true);
      try {
        await queryClient.fetchQuery(getProjectSessionsQueryOptions(project.id));
        sessionsPopover.open({ project, anchorEl });
      } catch (error) {
        showError((error as Error)?.message || 'Не удалось загрузить сессии');
      } finally {
        setProjectResourceLoading('sessions', project.id, false);
      }
    },
    [isProjectResourceLoading, queryClient, sessionsPopover.open, setProjectResourceLoading, showError]
  );
  const handleOpenNotesPopover = useCallback(
    async (project: Project, anchorEl: HTMLElement) => {
      if (isProjectResourceLoading('notes', project.id)) return;
      setProjectResourceLoading('notes', project.id, true);
      try {
        await queryClient.fetchQuery(getProjectNotesQueryOptions(project.id));
        notesPopover.open({ project, anchorEl });
      } catch (error) {
        showError((error as Error)?.message || 'Не удалось загрузить заметки');
      } finally {
        setProjectResourceLoading('notes', project.id, false);
      }
    },
    [isProjectResourceLoading, notesPopover.open, queryClient, setProjectResourceLoading, showError]
  );
  const handleOpenFilesPopover = useCallback(
    async (project: Project, anchorEl: HTMLElement) => {
      if (isProjectResourceLoading('files', project.id)) return;
      setProjectResourceLoading('files', project.id, true);
      try {
        await queryClient.fetchQuery(getProjectFilesQueryOptions(project.id));
        filesPopover.open({ project, anchorEl });
      } catch (error) {
        showError((error as Error)?.message || 'Не удалось загрузить файлы');
      } finally {
        setProjectResourceLoading('files', project.id, false);
      }
    },
    [filesPopover.open, isProjectResourceLoading, queryClient, setProjectResourceLoading, showError]
  );

  const handleEditSessionFromPopover = useCallback((session: Session) => {
    setSessionEditFromPopover(session);
  }, []);

  const handleSessionSavedFromPopover = useCallback(
    (session: Session) => {
      const patch = (prev: Project[] | undefined) =>
        prev
          ? prev.map((p) =>
              p.id === session.project_id
                ? { ...p, sessions: (p.sessions ?? []).map((s) => (s.id === session.id ? session : s)) }
                : p
            )
          : prev;
      patchProjectListCaches(queryClient, patch);
      setSessionEditFromPopover(null);
    },
    [queryClient]
  );

  useLayoutEffect(() => {
    const el = headerRowRef.current;
    if (!el) return;
    const sync = () => setHeaderHeight(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    sync();
    return () => ro.disconnect();
  }, []);

  const { compactReaders, compactStudio, setCompactReaders, setCompactStudio } = useTableCompactMode();
  const { capabilities } = useAuth();
  const hideColumnIds = useMemo(
    () => (capabilities.fonoteka.read ? EMPTY_HIDE_COLUMN_IDS : HAS_MUSIC_HIDE_IDS),
    [capabilities.fonoteka.read],
  );

  const {
    columns,
    allColumns,
    visibleColumns,
    gridTemplateColumns,
    toggleColumnVisibility,
    moveColumn,
    combos,
    atomicOrder,
    setComboTitle,
    setColumnWidth,
    clearColumnWidths,
    toggleComboSelection,
    getComboColumnsFor,
    getComboableColumnsForAnchor,
    resetColumnOrderToDefault,
    expandAllCombos,
    visibility,
  } = useProjectsTableColumns({ compactReaders, compactStudio, hideColumnIds });

  const noComboColumnIds = useMemo(
    () => getNoComboColumnIds(compactReaders, compactStudio),
    [compactReaders, compactStudio]
  );

  const handleCompactReadersChange = useCallback(
    (next: boolean) => {
      setCompactReaders(next);
      if (next) {
        setColumnWidth('readers', READERS_COLUMN_WIDTH_COMPACT);
      } else {
        clearColumnWidths(['readers']);
      }
    },
    [setCompactReaders, setColumnWidth, clearColumnWidths]
  );

  const handleCompactStudioChange = useCallback(
    (next: boolean) => {
      setCompactStudio(next);
      if (next) {
        setColumnWidth('studio', STUDIO_COLUMN_WIDTH_COMPACT);
      } else {
        clearColumnWidths(['studio']);
      }
    },
    [setCompactStudio, setColumnWidth, clearColumnWidths]
  );

  const { typeBadgesEnabled, toggleTypeBadge } = useTypeBadgeVisibility();
  const { plannerNameBadgeMode, setPlannerNameBadgeMode } = usePlannerNameBadgeMode();

  const { filtersEnabled, filters, setFilter, filterProjectIds } = useProjectsTableFilters();

  const filteredProjectIds = useMemo(() => {
    if (globalSearchActive) {
      return filterProjectIds(globalSearchProjectIds, resolveProject);
    }
    return filterProjectIds(projectIds, resolveProject).filter((projectId) => {
      const project = resolveProject(projectId);
      return project ? !isProjectExcludedFromActiveTable(project.state) : false;
    });
  }, [
    globalSearchActive,
    globalSearchProjectIds,
    filterProjectIds,
    projectIds,
    resolveProject,
  ]);

  const filteredProjects = useMemo(
    () => filteredProjectIds.map((projectId) => resolveProject(projectId)).filter((project): project is Project => !!project),
    [filteredProjectIds, resolveProject]
  );

  const dragDrop = useColumnDragDrop({
    columns,
    visibleColumns,
    gridTemplateColumns,
    moveColumn,
  });

  /**
   * Колонка «Название»: sticky к .projects-table__scroll.
   * `left`/`right` — inset от края scrollport (0), не offset в сетке.
   * Край прилипания: первая колонка → левый; последняя → правый; иначе — где больше колонок с другой стороны.
   */
  const nameColumnStickyEdge = useMemo((): 'hidden' | 'left' | 'right' => {
    const idx = visibleColumns.findIndex((c) => c.id === 'name');
    if (idx < 0) return 'hidden';
    const n = visibleColumns.length;
    if (n <= 1) return 'left';
    const before = idx;
    const after = n - 1 - idx;
    if (after === 0) return 'right';
    if (before === 0) return 'left';
    return before > after ? 'right' : 'left';
  }, [visibleColumns]);

  const [nameRightStuckActive, setNameRightStuckActive] = useState(false);

  const setScrollRef = useCallback((el: HTMLDivElement | null) => {
    (parentRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    setScrollReady(!!el);
  }, []);

  useScrollRestoration(parentRef, scrollReady, { storageKey: 'projects-scroll' });
  useMiddleButtonPanScroll(parentRef, scrollReady);

  /** Одна подписка на ширину скроллпорта — `--month-sep-viewport-w` для разделителей месяцев */
  useLayoutEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl) return;
    const syncViewportW = () => {
      scrollEl.style.setProperty('--month-sep-viewport-w', `${scrollEl.clientWidth}px`);
    };
    syncViewportW();
    const ro = new ResizeObserver(syncViewportW);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, [scrollReady]);

  const filteredInProcessSummary = useMemo(
    () => summarizeNonReadyProjectsWithTime(filteredProjects),
    [filteredProjects]
  );

  const readyMonthSeparatorDisplay = useMemo(() => {
    const out: Record<string, { projectCount: number | null; timeFormatted: string | null }> = {};
    for (const stat of readyMonthStats) {
      const { key } = parseYearMonthKey(stat.year_month);
      if (!expandedReadyMonths[key]) {
        out[key] = { projectCount: null, timeFormatted: null };
        continue;
      }
      if (readyMonthProjectLoading[key]) {
        out[key] = { projectCount: null, timeFormatted: null };
        continue;
      }
      const ids = readyProjectIdsByMonth[key];
      if (!ids) {
        out[key] = { projectCount: null, timeFormatted: null };
        continue;
      }
      const readyOnly = ids
        .map((projectId) => resolveProject(projectId))
        .filter(
          (project): project is Project =>
            !!project && isProjectExcludedFromActiveTable(project.state) && !project.is_archived
        );
      const t = summarizeLoadedProjectsWithTime(readyOnly);
      out[key] = {
        projectCount: readyOnly.length,
        timeFormatted: formatSecondsToHMS(t.totalSeconds),
      };
    }
    return out;
  }, [readyMonthStats, expandedReadyMonths, readyProjectIdsByMonth, readyMonthProjectLoading, resolveProject]);

  const archiveSeparatorSummary = useMemo(() => {
    if (!expandedArchive) {
      return { projectCount: null as number | null, timeFormatted: null as string | null };
    }
    if (archivedProjectLoading) {
      return { projectCount: null as number | null, timeFormatted: null as string | null };
    }
    if (archivedProjectIds.length === 0) {
      return { projectCount: null as number | null, timeFormatted: null as string | null };
    }
    const archivedOnly = archivedProjectIds
      .map((projectId) => resolveProject(projectId))
      .filter((project): project is Project => !!project && !!project.is_archived);
    return {
      projectCount: archivedOnly.length,
      timeFormatted: null as string | null,
    };
  }, [expandedArchive, archivedProjectLoading, archivedProjectIds, resolveProject]);

  const virtualRows: TableVirtualRow[] = useMemo(() => {
    if (globalSearchActive) {
      const rows: TableVirtualRow[] = [];
      for (const projectId of filteredProjectIds) {
        rows.push({ type: 'project', projectId });
      }
      return rows;
    }
    const rows: TableVirtualRow[] = [];
    const stats = readyMonthStats;
    const latestKey =
      stats.length > 0 ? parseYearMonthKey(stats[stats.length - 1]!.year_month).key : null;
    for (const stat of stats) {
      const { key } = parseYearMonthKey(stat.year_month);
      rows.push({ type: 'month', monthKey: key, stat, isLatestMonth: key === latestKey });
      if (expandedReadyMonths[key] && readyProjectIdsByMonth[key]) {
        const filteredReadyIds = filterProjectIds(readyProjectIdsByMonth[key] ?? [], resolveProject);
        for (const projectId of filteredReadyIds) {
          const project = resolveProject(projectId);
          if (!project || !isProjectExcludedFromActiveTable(project.state) || project.is_archived) continue;
          rows.push({ type: 'project', projectId });
        }
      }
    }
    for (const projectId of filteredProjectIds) {
      rows.push({ type: 'project', projectId });
    }
    if (archivedProjectCount > 0) {
      rows.push({ type: 'archive' });
      if (expandedArchive && archivedProjectIds.length > 0) {
        for (const projectId of filterProjectIds(archivedProjectIds, resolveProject)) {
          const project = resolveProject(projectId);
          if (!project?.is_archived) continue;
          rows.push({ type: 'project', projectId });
        }
      }
    }
    return rows;
  }, [
    readyMonthStats,
    expandedReadyMonths,
    readyProjectIdsByMonth,
    filteredProjectIds,
    filterProjectIds,
    resolveProject,
    archivedProjectCount,
    expandedArchive,
    archivedProjectIds,
    globalSearchActive,
  ]);

  /** Разделитель слева у «Названия» при right-sticky только в режиме прилипания (см. .projects-table__scroll--name-right-stuck). */
  const syncNameRightStuckBorder = useCallback(() => {
    if (nameColumnStickyEdge !== 'right') {
      setNameRightStuckActive(false);
      return;
    }
    const scrollEl = parentRef.current;
    const nameCell = headerRowRef.current?.querySelector('[data-column-id="name"]') as HTMLElement | null;
    if (!scrollEl || !nameCell) {
      setNameRightStuckActive(false);
      return;
    }
    if (scrollEl.scrollWidth <= scrollEl.clientWidth + 0.5) {
      setNameRightStuckActive(false);
      return;
    }
    const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
    /* В конце скролла колонка в «естественной» позиции — прилипание не активно, линия не нужна */
    if (scrollEl.scrollLeft >= maxScroll - 0.5) {
      setNameRightStuckActive(false);
      return;
    }
    const sr = scrollEl.getBoundingClientRect();
    const cr = nameCell.getBoundingClientRect();
    const visible = cr.left < sr.right && cr.right > sr.left;
    if (!visible) {
      setNameRightStuckActive(false);
      return;
    }
    /* К правому краю контентной области (без полосы прокрутки) — признак прилипания right:0 */
    const innerRight = sr.left + scrollEl.clientWidth;
    const rightAligned = Math.abs(cr.right - innerRight) < 10;
    /* Слева от колонки виден прокручиваемый контент — без этого линия зажигалась раньше реального sticky */
    const hasGapLeft = cr.left > sr.left + 2;
    setNameRightStuckActive(rightAligned && hasGapLeft);
  }, [nameColumnStickyEdge]);

  useLayoutEffect(() => {
    syncNameRightStuckBorder();
  }, [
    syncNameRightStuckBorder,
    dragDrop.gridWithDropIndicator,
    visibleColumns,
    headerHeight,
    virtualRows.length,
    scrollReady,
  ]);

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const onScrollOrResize = () => {
      requestAnimationFrame(() => syncNameRightStuckBorder());
    };
    const ro = new ResizeObserver(onScrollOrResize);
    el.addEventListener('scroll', onScrollOrResize, { passive: true });
    ro.observe(el);
    window.addEventListener('resize', onScrollOrResize);
    onScrollOrResize();
    return () => {
      el.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      ro.disconnect();
    };
  }, [syncNameRightStuckBorder, scrollReady]);

  /** Кэш размеров TanStack привязан к ключу; индекс обязан входить в ключ, иначе две строки
   * с одним project.id в списке (или устаревший размер с другой позиции) смешивают высоту. */
  const getVirtualItemKey = useCallback(
    (index: number) => {
      const row = virtualRows[index];
      if (!row) return String(index);
      if (row.type === 'month') return `m:${row.monthKey}@${index}`;
      if (row.type === 'archive') return `a:archive@${index}`;
      return `p:${row.projectId}@${index}`;
    },
    [virtualRows]
  );

  const allProjectsLookup = useMemo(() => {
    const map = new Map<string, Project>();
    for (const row of virtualRows) {
      if (row.type === 'project') {
        const project = resolveProject(row.projectId);
        if (project) map.set(row.projectId, project);
      }
    }
    return Array.from(map.values());
  }, [virtualRows, resolveProject]);

  const rowVirtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) =>
      virtualRows[index]?.type === 'month' || virtualRows[index]?.type === 'archive'
        ? MONTH_SEP_ESTIMATE_PX
        : getTableRowHeightPx(),
    overscan: 10,
    paddingStart: headerHeight,
    measureElement: measureTableVirtualRowElement,
    getItemKey: getVirtualItemKey,
  });

  const rowVirtualizerRef = useRef(rowVirtualizer);
  rowVirtualizerRef.current = rowVirtualizer;
  const measureRef = useCallback((el: HTMLDivElement | null) => {
    if (el) rowVirtualizerRef.current.measureElement(el);
  }, []);

  const totalHeight = rowVirtualizer.getTotalSize();
  const visibleVirtualItems = rowVirtualizer.getVirtualItems();

  const loadedSummaryProjectIds = useMemo(() => {
    const ids = new Set<string>(projectIds);
    for (const monthIds of Object.values(readyProjectIdsByMonth)) {
      for (const projectId of monthIds) ids.add(projectId);
    }
    for (const projectId of archivedProjectIds) ids.add(projectId);
    if (globalSearchActive) {
      for (const projectId of globalSearchProjectIds) ids.add(projectId);
    }
    return [...ids].sort();
  }, [
    projectIds,
    readyProjectIdsByMonth,
    archivedProjectIds,
    globalSearchActive,
    globalSearchProjectIds,
  ]);

  const { cellCommentSummaries, projectResourceSummaries } = useProjectTableSummaries(loadedSummaryProjectIds);

  const separatorVirtualRowIndices = useMemo(() => {
    const out: number[] = [];
    virtualRows.forEach((r, i) => {
      if (r.type === 'month' || r.type === 'archive') out.push(i);
    });
    return out;
  }, [virtualRows]);

  const virtualMeasurements = rowVirtualizer.measurementsCache;

  const [searchParams] = useSearchParams();
  const rawHighlight = searchParams.get(HIGHLIGHT_PARAM);
  const highlightProjectId = rawHighlight?.startsWith(`${HIGHLIGHT_TYPE_PROJECT}:`)
    ? rawHighlight.slice(HIGHLIGHT_TYPE_PROJECT.length + 1)
    : null;

  useEffect(() => {
    if (!scrollReady || !highlightProjectId || virtualRows.length === 0) return;
    const idx = virtualRows.findIndex(
      (row) => row.type === 'project' && row.projectId === highlightProjectId
    );
    if (idx >= 0) rowVirtualizer.scrollToIndex(idx, { align: 'center' });
  }, [scrollReady, highlightProjectId, virtualRows, rowVirtualizer]);

  const projectHighlightReady =
    scrollReady &&
    virtualRows.length > 0 &&
    (!highlightProjectId || allProjectsLookup.some((p) => p.id === highlightProjectId));
  useHighlightTarget('project', projectHighlightReady, parentRef);

  const handleHeaderContextMenu = useCallback((e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuAnchorPosition({ x: e.clientX, y: e.clientY });
    setMenuAnchorColumnId(columnId);
    setMenuOpen(true);
  }, []);

  const handleHeaderClick = useCallback((e: React.MouseEvent, columnId: string) => {
    if (!IS_TOUCH_DEVICE) return;
    setMenuAnchorPosition({ x: e.clientX, y: e.clientY });
    setMenuAnchorColumnId(columnId);
    setMenuOpen(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchorColumnId(null);
    setMenuAnchorPosition(null);
  }, []);

  const handleMonthSepMenuClose = useCallback(() => {
    setMonthSepMenuOpen(false);
    setMonthSepMenuPosition(null);
  }, []);

  const handleMonthSepBarContextMenu = useCallback((e: React.MouseEvent) => {
    if (!readyMonthStats.length) return;
    e.preventDefault();
    e.stopPropagation();
    setMonthSepMenuPosition({ x: e.clientX, y: e.clientY });
    setMonthSepMenuOpen(true);
  }, [readyMonthStats.length]);

  const isComboColumn = (id: string) => !!(combos[id]?.childIds?.length);

  const columnIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    columns.forEach((c, i) => map.set(c.id, i));
    return map;
  }, [columns]);

  const renderHeaderCell = (col: TableColumn, visibleIndex: number) => {
    const fullIndex = columns.findIndex((c) => c.id === col.id);
    const isLastColumn = visibleIndex === visibleColumns.length - 1;
    const isDragging = fullIndex === dragDrop.draggingColumnIndex;
    const showDropBefore = dragDrop.dropIndicatorVisibleIndex === visibleIndex;
    const isCombo = isComboColumn(col.id);
    const widthAdjustDisabled =
      (col.id === 'readers' && !compactReaders) || (col.id === 'studio' && !compactStudio);

    const colFilterType = PROJECT_COLUMN_FILTER_TYPES[col.id];
    const filterCellProps = colFilterType
      ? {
          filterType: colFilterType,
          currentRule: filters[col.id] ?? null,
          onFilterChange: setFilter,
          allProjects: allProjectsForFilter,
        }
      : undefined;

    const comboChildIds = isCombo ? (combos[col.id]?.childIds ?? []) : [];

    // Build filterable children list for combo filter popover
    const comboFilterChildren = isCombo
      ? comboChildIds
          .filter((id) => !!PROJECT_COLUMN_FILTER_TYPES[id])
          .map((id) => ({
            columnId: id,
            filterType: PROJECT_COLUMN_FILTER_TYPES[id]!,
            currentRule: filters[id] ?? null,
          }))
      : [];

    const headerContent = isCombo ? (
      <ComboHeaderCell
        title={col.title}
        comboId={col.id}
        width={col.width}
        onTitleChange={setComboTitle}
        onWidthChange={setColumnWidth}
        childColumnIds={comboChildIds}
        filters={filters}
        filtersEnabled={filtersEnabled}
        comboFilterChildren={comboFilterChildren}
        allProjects={allProjectsForFilter}
        onFilterChange={setFilter}
      >
        <span className="projects-table__header-cell-text">{col.title}</span>
      </ComboHeaderCell>
    ) : (
      <HeaderCellWithWidth
        columnId={col.id}
        width={col.width}
        defaultWidth={ALL_COLUMN_DEFS[col.id]?.width ?? '120px'}
        onWidthChange={setColumnWidth}
        widthAdjustDisabled={widthAdjustDisabled}
        filterProps={filterCellProps}
        filtersEnabled={filtersEnabled}
        searchPopoverTitle={col.title}
      >
        <span className="projects-table__header-cell-text">{col.title}</span>
      </HeaderCellWithWidth>
    );
    return (
      <Fragment key={col.id}>
        {showDropBefore && (
          <div
            className="projects-table__drop-indicator-cell"
            data-column-index={fullIndex}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              dragDrop.handleDragOver(e, fullIndex);
            }}
            onDrop={(e) => dragDrop.handleDrop(e, fullIndex)}
          />
        )}
        <div
          className={`projects-table__header-cell ${col.id === 'name' && nameColumnStickyEdge !== 'hidden' ? `projects-table__header-cell_name projects-table__header-cell_name_${nameColumnStickyEdge}` : ''} ${isDragging ? 'projects-table__header-cell_dragging' : ''}${isLastColumn ? ' projects-table__header-cell_last' : ''}`}
          data-column-index={fullIndex}
          data-column-id={col.id}
          draggable
          onContextMenu={(e) => handleHeaderContextMenu(e, col.id)}
          onClick={(e) => handleHeaderClick(e, col.id)}
          onDragStart={(e) => dragDrop.handleDragStart(e, fullIndex)}
          onDragEnd={dragDrop.handleDragEnd}
          onDragOver={(e) => dragDrop.handleDragOver(e, fullIndex)}
          onDrop={(e) => dragDrop.handleDrop(e, fullIndex)}
          onTouchStart={(e) => dragDrop.onTouchStart(e, fullIndex)}
          onTouchEnd={dragDrop.onTouchEnd}
          onTouchCancel={dragDrop.onTouchCancel}
        >
          {headerContent}
        </div>
      </Fragment>
    );
  };

  const headerContent = (
    <div
      ref={headerRowRef}
      className="projects-table__header-row"
      style={{ gridTemplateColumns: dragDrop.gridWithDropIndicator }}
      onDragLeave={dragDrop.handleHeaderRowDragLeave}
    >
      {visibleColumns.map((col, i) => renderHeaderCell(col, i))}
    </div>
  );

  const readyMonthSeparatorContextMenu =
    monthSepMenuOpen &&
    onMonthSepShowReadyTime &&
    onMonthSepShowInProgress &&
    onMonthSepShowProjectCount &&
    onMonthSepShowActiveProjectsCount &&
    onExpandAllReadyMonths &&
    onCollapseAllReadyMonths ? (
      <ReadyMonthSeparatorContextMenu
        isOpen={monthSepMenuOpen}
        onClose={handleMonthSepMenuClose}
        anchorPosition={monthSepMenuPosition}
        display={monthSepDisplay}
        setShowReadyTime={onMonthSepShowReadyTime}
        setShowInProgress={onMonthSepShowInProgress}
        setShowProjectCount={onMonthSepShowProjectCount}
        setShowActiveProjectsCount={onMonthSepShowActiveProjectsCount}
        onExpandAll={onExpandAllReadyMonths}
        onCollapseAll={onCollapseAllReadyMonths}
      />
    ) : null;

  const tableContextMenu = (
    <TableContextMenu
      isOpen={menuOpen}
      onClose={handleMenuClose}
      anchorPosition={menuAnchorPosition}
      anchorRef={headerRowRef}
      anchorColumnId={menuAnchorColumnId}
      columns={allColumns}
      atomicOrder={atomicOrder}
      combos={combos}
      visibility={visibility}
      toggleColumnVisibility={toggleColumnVisibility}
      toggleComboSelection={toggleComboSelection}
      getComboColumnsFor={getComboColumnsFor}
      getComboableColumnsForAnchor={getComboableColumnsForAnchor}
      typeBadgesEnabled={typeBadgesEnabled}
      toggleTypeBadge={toggleTypeBadge}
      plannerNameBadgeMode={plannerNameBadgeMode}
      setPlannerNameBadgeMode={setPlannerNameBadgeMode}
      resetColumnOrderToDefault={resetColumnOrderToDefault}
      expandAllCombos={expandAllCombos}
      noComboColumnIds={noComboColumnIds}
      compactReaders={compactReaders}
      compactStudio={compactStudio}
      onCompactReadersChange={handleCompactReadersChange}
      onCompactStudioChange={handleCompactStudioChange}
    />
  );

  const getProject = (id: string) => resolveProject(id) ?? allProjectsLookup.find((p) => p.id === id);

  if (virtualRows.length === 0) {
    return (
      <div className="projects-table">
        <div
          ref={setScrollRef}
          className={`projects-table__scroll${nameColumnStickyEdge === 'right' && nameRightStuckActive ? ' projects-table__scroll--name-right-stuck' : ''}`}
        >
          <div className="projects-table__inner">
            {headerContent}
            {isLoading || (globalSearchActive && globalSearchFetching) ? (
              <div className="projects-table__empty" aria-busy="true">
                {globalSearchActive ? 'Поиск…' : 'Загрузка таблицы...'}
              </div>
            ) : (
              <div className="projects-table__empty">
                {globalSearchActive ? 'Ничего не найдено' : 'Нет проектов'}
              </div>
            )}
          </div>
        </div>
        {tableContextMenu}
        {readyMonthSeparatorContextMenu}
      </div>
    );
  }

  return (
    <div className="projects-table">
      <div
        ref={setScrollRef}
        className={`projects-table__scroll${nameColumnStickyEdge === 'right' && nameRightStuckActive ? ' projects-table__scroll--name-right-stuck' : ''}`}
      >
        <div className="projects-table__inner" style={{ height: `${totalHeight}px` }}>
          {headerContent}
          {separatorVirtualRowIndices.length > 0 ? (
            <div className="projects-table__month-sticky-layer">
              {separatorVirtualRowIndices.map((rowIndex, ord) => {
                const item = virtualRows[rowIndex];
                if (!item || (item.type !== 'month' && item.type !== 'archive')) return null;
                const rangeStart = virtualMeasurements[rowIndex]?.start;
                if (rangeStart == null) return null;
                const nextIdx = separatorVirtualRowIndices[ord + 1];
                const rangeEnd =
                  nextIdx !== undefined ? virtualMeasurements[nextIdx]?.start ?? totalHeight : totalHeight;
                const sectionHeight = rangeEnd - rangeStart;
                if (sectionHeight <= 0) return null;

                if (item.type === 'archive') {
                  const sep = archiveSeparatorSummary;
                  return (
                    <div
                      key="sticky-archive-sep"
                      className="projects-table__month-sticky-range"
                      style={{ top: rangeStart, height: sectionHeight }}
                    >
                      <div className="projects-table__month-sticky-pin" style={{ top: headerHeight }}>
                        <ReadyMonthSeparatorBar
                          variant="archive"
                          monthLabel="Архив"
                          projectCount={sep.projectCount}
                          timeFormatted={null}
                          inProcess={{
                            projectCount: filteredProjects.length,
                            totalSeconds: filteredInProcessSummary.totalSeconds,
                            countWithTime: filteredInProcessSummary.countWithTime,
                          }}
                          expanded={!!expandedArchive}
                          onToggle={() => onToggleArchive?.()}
                          isLoadingProjects={!!expandedArchive && !!archivedProjectLoading}
                          showReadyTime={false}
                          showInProgressTime={false}
                          showProjectCount={monthSepDisplay.showProjectCount}
                          showActiveProjectsCount={false}
                        />
                      </div>
                    </div>
                  );
                }

                const label = formatMonthLabelRu(item.stat.year_month);
                const sep = readyMonthSeparatorDisplay[item.monthKey] ?? {
                  projectCount: null,
                  timeFormatted: null,
                };
                return (
                  <div
                    key={`sticky-month-${item.monthKey}`}
                    className="projects-table__month-sticky-range"
                    style={{ top: rangeStart, height: sectionHeight }}
                  >
                    <div className="projects-table__month-sticky-pin" style={{ top: headerHeight }}>
                      <ReadyMonthSeparatorBar
                        monthLabel={label}
                        projectCount={sep.projectCount}
                        timeFormatted={sep.timeFormatted}
                        inProcess={{
                          projectCount: filteredProjects.length,
                          totalSeconds: filteredInProcessSummary.totalSeconds,
                          countWithTime: filteredInProcessSummary.countWithTime,
                        }}
                        expanded={!!expandedReadyMonths[item.monthKey]}
                        onToggle={() => onToggleReadyMonth?.(item.monthKey)}
                        isLoadingProjects={
                          !!expandedReadyMonths[item.monthKey] && !!readyMonthProjectLoading[item.monthKey]
                        }
                        showReadyTime={monthSepDisplay.showReadyTime}
                        showInProgressTime={monthSepDisplay.showInProgress && item.isLatestMonth}
                        showProjectCount={monthSepDisplay.showProjectCount}
                        showActiveProjectsCount={monthSepDisplay.showActiveProjectsCount && item.isLatestMonth}
                        onBarContextMenu={handleMonthSepBarContextMenu}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
          {visibleVirtualItems.map((virtualRow) => {
            const item = virtualRows[virtualRow.index]!;
            if (item.type === 'month') {
              return (
                <ReadyMonthSeparatorSpacer
                  key={`month-spacer-${item.monthKey}`}
                  dataIndex={virtualRow.index}
                  top={virtualRow.start}
                  measureRef={measureRef}
                />
              );
            }
            if (item.type === 'archive') {
              return (
                <ReadyMonthSeparatorSpacer
                  key="archive-spacer"
                  dataIndex={virtualRow.index}
                  top={virtualRow.start}
                  measureRef={measureRef}
                />
              );
            }
            const project = resolveProject(item.projectId);
            if (!project) return null;
            return (
              <ProjectTableRow
                key={project.id}
                projectId={project.id}
                initialProject={project}
                visibleColumns={visibleColumns}
                combos={combos}
                atomicOrder={atomicOrder}
                onDelete={onDelete}
                onStateChange={onStateChange}
                onProjectTypeChange={onProjectTypeChange}
                onOpenSessionsModal={handleOpenSessionsModal}
                onOpenNotesPopover={handleOpenNotesPopover}
                onOpenFilesPopover={handleOpenFilesPopover}
                onOpenReviewCheckEmail={onOpenReviewCheckEmail}
                onOpenProductionHandoffEmail={onOpenProductionHandoffEmail}
                onOpenPreArchiveUploadEmail={onOpenPreArchiveUploadEmail}
                onOpenMountingCompleteEmail={onOpenMountingCompleteEmail}
                onOpenCorrectionsAppliedEmail={onOpenCorrectionsAppliedEmail}
                onOpenProjectReviewedEmail={onOpenProjectReviewedEmail}
                isDeleting={deletingProjectId === project.id}
                typeBadgesEnabled={typeBadgesEnabled}
                plannerNameBadgeMode={plannerNameBadgeMode}
                compactReaders={compactReaders}
                compactStudio={compactStudio}
                top={virtualRow.start}
                gridTemplateColumns={dragDrop.gridWithDropIndicator}
                draggingColumnIndex={dragDrop.draggingColumnIndex}
                dropIndicatorVisibleIndex={dragDrop.dropIndicatorVisibleIndex}
                measureRef={measureRef}
                dataIndex={virtualRow.index}
                columnIndexMap={columnIndexMap}
                cellCommentSummary={cellCommentSummaries[project.id]}
                resourceSummary={projectResourceSummaries[project.id]}
                resourceLoadingByColumn={{
                  sessions: isProjectResourceLoading('sessions', project.id),
                  notes: isProjectResourceLoading('notes', project.id),
                  files: isProjectResourceLoading('files', project.id),
                }}
                nameColumnStickyEdge={nameColumnStickyEdge}
                searchHighlightWords={searchHighlightWords}
              />
            );
          })}
        </div>
      </div>
      {tableContextMenu}
      {readyMonthSeparatorContextMenu}
      {sessionsPopover.exiting && (
        <ProjectSessionsModal
          key={`sessions-${sessionsPopover.exiting.project.id}`}
          open={false}
          onClose={sessionsPopover.clearExiting}
          project={getProject(sessionsPopover.exiting.project.id) ?? sessionsPopover.exiting.project}
          anchorEl={sessionsPopover.exiting.anchorEl}
        />
      )}
      {sessionsPopover.active && (
        <ProjectSessionsModal
          key={`sessions-${sessionsPopover.active.project.id}`}
          open
          onClose={sessionsPopover.close}
          project={getProject(sessionsPopover.active.project.id) ?? sessionsPopover.active.project}
          anchorEl={sessionsPopover.active.anchorEl}
          onEditSession={handleEditSessionFromPopover}
        />
      )}
      {sessionEditFromPopover && (
        <SessionModal
          open
          onClose={() => setSessionEditFromPopover(null)}
          onSaved={handleSessionSavedFromPopover}
          selection={null}
          editingSession={sessionEditFromPopover}
          projects={allProjectsLookup}
          allowedSessionTypes={['запись', 'правки']}
        />
      )}
      {notesPopover.exiting && (
        <ProjectNotesPopover
          key={`notes-${notesPopover.exiting.project.id}`}
          open={false}
          onClose={notesPopover.clearExiting}
          project={getProject(notesPopover.exiting.project.id) ?? notesPopover.exiting.project}
          anchorEl={notesPopover.exiting.anchorEl}
        />
      )}
      {notesPopover.active && (
        <ProjectNotesPopover
          key={`notes-${notesPopover.active.project.id}`}
          open
          onClose={notesPopover.close}
          project={getProject(notesPopover.active.project.id) ?? notesPopover.active.project}
          anchorEl={notesPopover.active.anchorEl}
        />
      )}
      {filesPopover.exiting && (
        <ProjectFilesPopover
          key={`files-${filesPopover.exiting.project.id}`}
          open={false}
          onClose={filesPopover.clearExiting}
          project={getProject(filesPopover.exiting.project.id) ?? filesPopover.exiting.project}
          anchorEl={filesPopover.exiting.anchorEl}
        />
      )}
      {filesPopover.active && (
        <ProjectFilesPopover
          key={`files-${filesPopover.active.project.id}`}
          open
          onClose={filesPopover.close}
          project={getProject(filesPopover.active.project.id) ?? filesPopover.active.project}
          anchorEl={filesPopover.active.anchorEl}
        />
      )}
    </div>
  );
}

export function ProjectsTable(props: ProjectsTableProps) {
  return (
    <CellCommentsTableProvider>
      <ProjectsTableInner {...props} />
    </CellCommentsTableProvider>
  );
}
